import { Suspense, useEffect, useRef, useState } from 'react';
import { graphql, useLazyLoadQuery, useMutation } from 'react-relay/hooks';
import { stringFormat, T } from 'src/shared/helpers';
import WarningIcon from 'src/shared/material_icons/alert/warning_amber.svg';
import styled from 'styled-components';
import { Link, PrimaryButton } from '../buttons';
import { CustomErrorBoundary } from '../customErrorBoundary';
import { Dialog, useDialogManager } from '../dialog';
import { NotificationBanner } from '../notificationBanner';
import { showErrorNotification, showWarningNotification } from '../notificationToaster';
import { Spinner } from '../spinner';
import Stack from '../stack/stack';
import { ZTooltip } from '../ztooltip';
import { shopifyAppEmbedBannerMarkActiveMutation } from './__generated__/shopifyAppEmbedBannerMarkActiveMutation.graphql';
import { shopifyAppEmbedBannerQuery } from './__generated__/shopifyAppEmbedBannerQuery.graphql';
import { shopifyAppEmbedBannerStatusQuery } from './__generated__/shopifyAppEmbedBannerStatusQuery.graphql';

/** Adds a sales channel ID to a state set, keeping the same reference when nothing changes. */
const addIDToSet = (ids: Set<string>, salesChannelID: string) => {
    if (ids.has(salesChannelID)) return ids;

    const nextIDs = new Set(ids);
    nextIDs.add(salesChannelID);

    return nextIDs;
};

/** The confirmation step a store is currently busy with, or absent when it is idle. */
type PendingPhase = 'checking' | 'saving';

/** Sets the pending phase of a store, keeping the same reference when nothing changes. */
const setPendingPhase = (phases: Map<string, PendingPhase>, salesChannelID: string, phase: PendingPhase | null) => {
    if ((phases.get(salesChannelID) ?? null) === phase) return phases;

    const nextPhases = new Map(phases);
    if (phase) nextPhases.set(salesChannelID, phase);
    else nextPhases.delete(salesChannelID);

    return nextPhases;
};

const StyledContainer = styled.div`
    width: 100%;
    margin-top: 20px;
`;

const StyledNotificationBanner = styled(NotificationBanner)`
    background: #ffffff;
    border-color: #f0d7ad;
    border-left: 4px solid #f4a900;

    > div {
        padding: 0;
        align-items: stretch;
    }
`;

const BannerContent = styled(Stack)`
    gap: 0;
`;

const BannerHeader = styled(Stack)`
    gap: 14px;
    padding: 22px 24px 20px;
    background: #fff8ed;
    border-bottom: 1px solid #f0d7ad;
`;

const BannerTitleRow = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
`;

const BannerWarningIcon = styled(WarningIcon)`
    flex: 0 0 auto;
    width: 20px;
    height: 20px;
`;

const BannerTitle = styled.strong`
    color: #263238;
    font-size: 16px;
    font-weight: 600;
`;

const BannerDescription = styled.div`
    display: grid;
    gap: 10px;
    color: #263238;
    max-width: 900px;
`;

const BannerInstructions = styled.ol`
    display: grid;
    gap: 8px;
    margin: 0;
    padding-left: 24px;

    li {
        padding-left: 4px;
    }
`;

const StatusBadge = styled.span`
    display: inline-flex;
    align-items: center;
    min-height: 24px;
    padding: 2px 9px;
    border-radius: 999px;
    background: #fff0d7;
    color: #8a5b00;
    font-size: 12px;
    font-weight: 600;
`;

const AppEmbedRows = styled(Stack)`
    width: 100%;
    gap: 0;
`;

const StoreTable = styled.div`
    width: 100%;
    padding: 0 24px 22px;
    background: #ffffff;
`;

const StoreTableHeader = styled.div`
    display: grid;
    grid-template-columns: minmax(180px, 1.1fr) minmax(0, 2fr) minmax(220px, 1fr);
    align-items: center;
    gap: 24px;
    padding: 12px 0;
    border-top: 1px solid rgba(0, 0, 0, 0.12);
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);
    color: #546e7a;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;

    @media screen and (max-width: 900px) {
        display: none;
    }
`;

const AppEmbedRow = styled.div`
    display: grid;
    grid-template-columns: minmax(180px, 1.1fr) minmax(0, 2fr) minmax(220px, 1fr);
    align-items: center;
    gap: 24px;
    width: 100%;
    padding: 16px 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);

    &:last-child {
        border-bottom: 0;
        padding-bottom: 0;
    }

    @media screen and (max-width: 900px) {
        grid-template-columns: 1fr;
        gap: 12px;
    }
`;

const StoreDetails = styled(Stack)`
    gap: 4px;
    min-width: 0;
`;

const StoreName = styled.strong`
    overflow-wrap: anywhere;
`;

const StoreStatus = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: #8a5b00;
    font-size: 12px;

    &::before {
        width: 7px;
        height: 7px;
        border-radius: 50%;
        background: #f4a900;
        content: '';
    }
`;

const StoreLink = styled(Link)`
    display: inline-block;
    overflow-wrap: anywhere;
    line-height: 1.4;
`;

const ConfirmButton = styled(PrimaryButton)`
    height: auto;
    justify-content: center;
    justify-self: end;
    max-width: 280px;
    min-height: 34px;
    padding: 8px 12px;
    text-align: center;
    white-space: normal;
    line-height: 1.25;
`;

const ConfirmButtonTooltipContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    width: 100%;
`;

const ConfirmButtonSpinner = styled(Spinner)`
    flex: 0 0 auto;
    width: 14px;
    height: 14px;

    && .path {
        stroke: currentColor;
    }
`;

type ShopifyAppEmbedBannerProps = {
    onStoreConfirmed?: (remainingStores: number, salesChannelID: string) => void;
    salesChannelIDs?: readonly string[];
    compact?: boolean;
    className?: string;
};

type AppEmbedStatus = shopifyAppEmbedBannerStatusQuery['response']['shopifyAppEmbedStatus'];

const AppEmbedStatusVerifier = ({ salesChannelID, onResult }: { salesChannelID: string; onResult: (status: AppEmbedStatus) => void }) => {
    const { shopifyAppEmbedStatus } = useLazyLoadQuery<shopifyAppEmbedBannerStatusQuery>(
        graphql`
            query shopifyAppEmbedBannerStatusQuery($salesChannelID: ID!) {
                shopifyAppEmbedStatus(salesChannelID: $salesChannelID)
            }
        `,
        { salesChannelID },
        { fetchPolicy: 'network-only' }
    );
    const hasReported = useRef(false);

    useEffect(() => {
        if (hasReported.current) return;
        hasReported.current = true;
        onResult(shopifyAppEmbedStatus);
    }, [onResult, shopifyAppEmbedStatus]);

    return null;
};

const AppEmbedStatusError = ({ onError }: { onError: () => void }) => {
    const hasReported = useRef(false);

    useEffect(() => {
        if (hasReported.current) return;
        hasReported.current = true;
        onError();
    }, [onError]);

    return null;
};

const _ShopifyAppEmbedBanner = ({ onStoreConfirmed, salesChannelIDs, compact, className }: ShopifyAppEmbedBannerProps) => {
    const { showDialog, closeDialog } = useDialogManager();
    const { shopifyAppEmbedsToEnable } = useLazyLoadQuery<shopifyAppEmbedBannerQuery>(
        graphql`
            query shopifyAppEmbedBannerQuery {
                shopifyAppEmbedsToEnable {
                    salesChannelID
                    shop
                    appEmbedDeepLink
                }
            }
        `,
        {},
        { fetchPolicy: 'network-only' }
    );
    const [markAppEmbedActive] = useMutation<shopifyAppEmbedBannerMarkActiveMutation>(graphql`
        mutation shopifyAppEmbedBannerMarkActiveMutation($salesChannelID: ID!) {
            shopifyMarkAppEmbedActive(salesChannelID: $salesChannelID)
        }
    `);
    const [dismissedSalesChannelIDs, setDismissedSalesChannelIDs] = useState<Set<string>>(() => new Set());
    const confirmedSalesChannelIDs = useRef<Set<string>>(new Set());
    const [openedAppEmbedSalesChannelIDs, setOpenedAppEmbedSalesChannelIDs] = useState<Set<string>>(() => new Set());
    const [pendingPhasesBySalesChannelID, setPendingPhasesBySalesChannelID] = useState<Map<string, PendingPhase>>(() => new Map());

    const relevantStores = shopifyAppEmbedsToEnable.filter(({ salesChannelID }) => !salesChannelIDs || salesChannelIDs.includes(salesChannelID));
    const stores = relevantStores.filter(({ salesChannelID }) => !dismissedSalesChannelIDs.has(salesChannelID));

    if (stores.length === 0) {
        return null;
    }

    const handleMarkActive = (salesChannelID: string) => {
        setPendingPhasesBySalesChannelID((currentPhases) => setPendingPhase(currentPhases, salesChannelID, 'checking'));
    };

    const handleVerificationError = (salesChannelID: string) => {
        setPendingPhasesBySalesChannelID((currentPhases) => setPendingPhase(currentPhases, salesChannelID, null));
        showErrorNotification({ message: T._('Unable to update the Shopify app embed status.', 'Admin') });
    };

    const handleStatus = async (salesChannelID: string, status: AppEmbedStatus) => {
        const setPhase = (phase: PendingPhase | null) =>
            setPendingPhasesBySalesChannelID((currentPhases) => setPendingPhase(currentPhases, salesChannelID, phase));

        try {
            // NOT_APPLICABLE means the app embed cannot be verified (for example the store did not grant the
            // read_themes permission), so the merchant confirmation is still trusted.
            if (status === 'DISABLED') {
                showWarningNotification({
                    message: T._(
                        'Zakeke is still not active on your Shopify storefront. In the theme editor, turn on the Zakeke app embed and click Save, then confirm again.',
                        'Admin'
                    )
                });
                return;
            }

            setPhase('saving');

            const { promise, resolve, reject } = Promise.withResolvers<void>();
            markAppEmbedActive({
                variables: { salesChannelID },
                onCompleted: (response) => {
                    if (!response.shopifyMarkAppEmbedActive) {
                        reject(new Error('Unable to update the Shopify app embed status.'));
                        return;
                    }

                    resolve();
                },
                onError: reject
            });
            await promise;

            if (!confirmedSalesChannelIDs.current.has(salesChannelID)) {
                confirmedSalesChannelIDs.current.add(salesChannelID);
                setDismissedSalesChannelIDs(new Set(confirmedSalesChannelIDs.current));
                onStoreConfirmed?.(relevantStores.filter((store) => !confirmedSalesChannelIDs.current.has(store.salesChannelID)).length, salesChannelID);
            }
        } catch {
            showErrorNotification({ message: T._('Unable to update the Shopify app embed status.', 'Admin') });
        } finally {
            setPhase(null);
        }
    };

    if (compact) {
        return (
            <StyledContainer className={className}>
                <NotificationBanner
                    type='CRITICAL'
                    title={
                        stores.length === 1
                            ? T._('Enable Zakeke on your Shopify store', 'Admin')
                            : stringFormat(T._('Enable Zakeke on your Shopify stores', 'Admin'), stores.length.toString())
                    }
                    content={
                        stores.length === 1
                            ? T._(
                                  'A connected Shopify store still needs Zakeke to be enabled. Enable the Zakeke app embed in its theme to make published products usable on its storefront.',
                                  'Admin'
                              )
                            : stringFormat(
                                  T._(
                                      '{0} connected Shopify stores still need Zakeke to be enabled. Enable the Zakeke app embed in each store listed to make published products usable on its storefront.',
                                      'Admin'
                                  ),
                                  stores.length.toString()
                              )
                    }
                    buttons={[
                        {
                            label: T._('Enable Zakeke on Shopify', 'Admin'),
                            onClick: () =>
                                showDialog(
                                    'test-page-shopify-app-embed',
                                    <Dialog size='large' closeOnOverlayClick>
                                        <ShopifyAppEmbedBanner
                                            salesChannelIDs={stores.map((store) => store.salesChannelID)}
                                            onStoreConfirmed={(remainingStores, salesChannelID) => {
                                                setDismissedSalesChannelIDs((ids) => addIDToSet(ids, salesChannelID));
                                                onStoreConfirmed?.(remainingStores, salesChannelID);
                                                if (remainingStores === 0) closeDialog('test-page-shopify-app-embed');
                                            }}
                                        />
                                    </Dialog>
                                )
                        }
                    ]}
                />
            </StyledContainer>
        );
    }

    return (
        <StyledContainer className={className}>
            {[...pendingPhasesBySalesChannelID]
                .filter(([, phase]) => phase === 'checking')
                .map(([salesChannelID]) => (
                    <CustomErrorBoundary key={salesChannelID} render={() => <AppEmbedStatusError onError={() => handleVerificationError(salesChannelID)} />}>
                        <Suspense fallback={null}>
                            <AppEmbedStatusVerifier salesChannelID={salesChannelID} onResult={(status) => handleStatus(salesChannelID, status)} />
                        </Suspense>
                    </CustomErrorBoundary>
                ))}
            <StyledNotificationBanner
                type='WARNING'
                buttonsPosition='FOOTER'
                content={
                    <BannerContent>
                        <BannerHeader>
                            <BannerTitleRow>
                                <BannerWarningIcon aria-hidden='true' />
                                <BannerTitle>{T._('Make Zakeke work on your Shopify store', 'Admin')}</BannerTitle>
                                <StatusBadge>{T._('Action required', 'Admin')}</StatusBadge>
                            </BannerTitleRow>
                            <BannerDescription>
                                <div>
                                    {T._(
                                        'Zakeke must be enabled in Shopify before it can work on your storefront. Complete the setup for every store listed below.',
                                        'Admin'
                                    )}
                                </div>
                                <BannerInstructions>
                                    <li>
                                        {T._(
                                            'In the Shopify app embed column, click the link that starts with “Open App embeds for”. Use the link on the same row as your store.',
                                            'Admin'
                                        )}
                                    </li>
                                    <li>
                                        {T._(
                                            'Shopify opens the theme editor with Zakeke already selected. Check that the Zakeke app embed is enabled. If the switch is off, turn it on.',
                                            'Admin'
                                        )}
                                    </li>
                                    <li>{T._('Click Save in Shopify to apply the change.', 'Admin')}</li>
                                    <li>{T._('Return to this page and click the confirmation button for the same store.', 'Admin')}</li>
                                </BannerInstructions>
                            </BannerDescription>
                        </BannerHeader>
                        <StoreTable>
                            <StoreTableHeader>
                                <span>{T._('Store', 'Admin')}</span>
                                <span>{T._('Shopify app embed', 'Admin')}</span>
                                <span>{T._('Confirmation', 'Admin')}</span>
                            </StoreTableHeader>
                            <AppEmbedRows>
                                {stores.map((store) => {
                                    const hasOpenedAppEmbed = openedAppEmbedSalesChannelIDs.has(store.salesChannelID);
                                    const pendingPhase = pendingPhasesBySalesChannelID.get(store.salesChannelID);

                                    return (
                                        <AppEmbedRow key={store.salesChannelID}>
                                            <StoreDetails>
                                                <StoreName>{store.shop}</StoreName>
                                                <StoreStatus>{T._('Needs setup', 'Admin')}</StoreStatus>
                                            </StoreDetails>
                                            <StoreLink
                                                href={store.appEmbedDeepLink}
                                                target='_blank'
                                                rel='noopener noreferrer'
                                                onClick={() => {
                                                    setOpenedAppEmbedSalesChannelIDs((currentIDs) => addIDToSet(currentIDs, store.salesChannelID));
                                                }}
                                            >
                                                {T._('Open App embeds for', 'Admin')} {store.shop}
                                            </StoreLink>
                                            <ZTooltip
                                                title={
                                                    hasOpenedAppEmbed || pendingPhase
                                                        ? undefined
                                                        : T._(
                                                              'First, click the App embeds link and follow the steps in Shopify. When you finish, come back here and click this button to confirm.',
                                                              'Admin'
                                                          )
                                                }
                                                position='top'
                                                addContainer
                                            >
                                                <ConfirmButtonTooltipContainer>
                                                    <ConfirmButton
                                                        type='button'
                                                        $size='small'
                                                        disabled={!!pendingPhase || !hasOpenedAppEmbed}
                                                        onClick={() => handleMarkActive(store.salesChannelID)}
                                                    >
                                                        {pendingPhase ? (
                                                            <>
                                                                <ConfirmButtonSpinner aria-hidden='true' />
                                                                {pendingPhase === 'saving' ? T._('Saving…', 'Admin') : T._('Checking on Shopify…', 'Admin')}
                                                            </>
                                                        ) : (
                                                            <>
                                                                {T._("I've enabled it for", 'Admin')} {store.shop}
                                                            </>
                                                        )}
                                                    </ConfirmButton>
                                                </ConfirmButtonTooltipContainer>
                                            </ZTooltip>
                                        </AppEmbedRow>
                                    );
                                })}
                            </AppEmbedRows>
                        </StoreTable>
                    </BannerContent>
                }
            />
        </StyledContainer>
    );
};

const ShopifyAppEmbedBanner = (props: ShopifyAppEmbedBannerProps) => (
    <CustomErrorBoundary render={() => null}>
        <Suspense
            fallback={
                props.compact ? null : (
                    <StyledContainer className={props.className}>
                        <Spinner />
                    </StyledContainer>
                )
            }
        >
            <_ShopifyAppEmbedBanner {...props} />
        </Suspense>
    </CustomErrorBoundary>
);

export default ShopifyAppEmbedBanner;
